import React, { useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { toast } from "react-toastify";

export function base64FromCropperDataUrl(dataUrl) {
    if (!dataUrl) {
        return "";
    }
    const commaIndex = dataUrl.indexOf(",");
    return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
}

function SupportFileAttachment({
    value,
    onChange,
    inputId = "SupportAttachInput",
    compact = false,
    disabled = false,
}) {
    const [showModal, setShowModal] = useState(false);
    const [imageSelected, setImageSelected] = useState(false);
    const [image, setImage] = useState("");
    const cropperRef = useRef(null);

    const resetCropperState = () => {
        setImageSelected(false);
        setImage("");
        cropperRef.current = null;
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetCropperState();
    };

    const handleOpenModal = () => {
        if (disabled) {
            return;
        }
        resetCropperState();
        setShowModal(true);
    };

    const onFileChange = (e) => {
        e.preventDefault();
        const files = e.target?.files;
        if (!files || !files[0]) {
            return;
        }

        if (!files[0].type.match(/^image\/(jpeg|png)$/i)) {
            toast.error("Please choose a JPEG or PNG image.", {
                position: toast.POSITION.TOP_RIGHT,
            });
            e.target.value = "";
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setImage(reader.result);
            setImageSelected(true);
        };
        reader.onerror = () => {
            toast.error("Could not read the selected image.", {
                position: toast.POSITION.TOP_RIGHT,
            });
        };
        reader.readAsDataURL(files[0]);
        e.target.value = "";
    };

    const saveCrop = () => {
        const cropper = cropperRef.current;
        if (!cropper) {
            toast.error("Cropper is not ready yet. Please wait a moment and try again.", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }

        const canvas = cropper.getCroppedCanvas({
            maxWidth: 1600,
            maxHeight: 1600,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: "high",
        });

        if (!canvas) {
            toast.error("Could not crop the image. Please try another photo.", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }

        onChange(base64FromCropperDataUrl(canvas.toDataURL("image/jpeg", 0.92)));
        handleCloseModal();
        toast.success("File attached successfully", {
            position: toast.POSITION.TOP_RIGHT,
        });
    };

    const clearAttachment = (e) => {
        e.stopPropagation();
        onChange("");
    };

    return (
        <>
            {compact ? (
                <button
                    type="button"
                    className={`reply-attach-btn${value ? " has-file" : ""}`}
                    onClick={handleOpenModal}
                    disabled={disabled}
                    title={value ? "Change attachment" : "Attach photo"}
                >
                    <i className="ri-attachment-2" />
                </button>
            ) : (
                <div className="d-flex align-items-center flex-wrap gap-2">
                    <button
                        type="button"
                        className="button button-gradient"
                        onClick={handleOpenModal}
                        disabled={disabled}
                    >
                        <span>{value ? "Change attachment" : "Attach file (photo)"}</span>
                    </button>
                    {value ? (
                        <span className="small text-success d-inline-flex align-items-center gap-1">
                            <i className="ri-check-line" />
                            Image attached
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 ms-1"
                                onClick={clearAttachment}
                            >
                                Remove
                            </button>
                        </span>
                    ) : null}
                </div>
            )}

            <Modal
                show={showModal}
                onHide={handleCloseModal}
                centered
                enforceFocus={false}
                restoreFocus={false}
                dialogClassName="support-attachment-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Upload attachment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="upload-photo-wrap support-attachment-cropper-wrap">
                        {imageSelected ? (
                            <>
                                <div
                                    className="support-attachment-cropper-container"
                                    style={{ minHeight: 360 }}
                                >
                                    <Cropper
                                        key={image}
                                        src={image}
                                        className="support-attachment-cropper"
                                        style={{ height: 360, width: "100%" }}
                                        aspectRatio={NaN}
                                        viewMode={2}
                                        dragMode="move"
                                        guides={true}
                                        background={false}
                                        responsive={true}
                                        autoCropArea={0.9}
                                        checkOrientation={true}
                                        minCropBoxWidth={40}
                                        minCropBoxHeight={40}
                                        onInitialized={(instance) => {
                                            cropperRef.current = instance;
                                        }}
                                    />
                                </div>
                                <p className="small text-muted mt-2 mb-0">
                                    Drag to reposition, then click Save photo.
                                </p>
                            </>
                        ) : (
                            <div className="choose-photo">
                                <label htmlFor={inputId}>
                                    <i className="ri-upload-cloud-line" />
                                    <span>Browse photo</span>
                                    <input
                                        id={inputId}
                                        accept="image/jpeg,image/png"
                                        type="file"
                                        onChange={onFileChange}
                                    />
                                </label>
                            </div>
                        )}
                    </div>
                    <div className="d-flex justify-content-center gap-2 mt-3 flex-wrap">
                        {imageSelected ? (
                            <button
                                type="button"
                                className="button button-outline-primary"
                                onClick={resetCropperState}
                            >
                                Choose another
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className="button button-outline-primary"
                            onClick={handleCloseModal}
                        >
                            Close
                        </button>
                        <button
                            type="button"
                            className="button button-primary"
                            onClick={saveCrop}
                            disabled={!imageSelected}
                        >
                            Save photo
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default SupportFileAttachment;
